import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import axios, { API, formatError, inr } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { ClosuresNotice } from "./ClosuresNotice";

const EMPTY = { name: "", phone: "", email: "", company: "", quantity_t: "", address: "", notes: "" };

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5"><Label htmlFor={id} className="text-[var(--sage)] text-xs font-mono uppercase tracking-wider">{label}</Label>{children}</div>
);

export const OrderDialog = ({ open, onOpenChange, products, initialSlug, prefill, closures }) => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (open) {
      setSlug(initialSlug || products[0]?.slug || "");
      setDone(null);
      if (prefill) {
        setForm({
          ...EMPTY,
          name: prefill.name || "",
          phone: prefill.phone || "",
          email: prefill.email || "",
          address: prefill.address || "",
          quantity_t: prefill.quantity_t ? String(prefill.quantity_t) : "",
        });
      }
    }
  }, [open, initialSlug, products, prefill]);

  const product = products.find((p) => p.slug === slug);
  const qty = Number(form.quantity_t) || 0;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/orders`, { ...form, product_slug: slug, quantity_t: qty, lang });
      setDone({ ...res.data, phone: form.phone, email: form.email });
      setForm(EMPTY);
      toast.success(t("order.toast", { ref: res.data.ref }));
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-lenis-prevent data-testid="order-modal" className="max-w-xl max-h-[92vh] overflow-y-auto rounded-[28px] border hairline bg-[var(--forest-2)] text-[var(--cream)]">
        {done ? (
          <div data-testid="order-success" className="py-10 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[var(--mint)]" />
            <h3 className="mt-6 font-display text-3xl font-bold">{t("order.doneTitle")}</h3>
            <p className="mt-2 text-[var(--sage)]">{t("order.refPre")}<span data-testid="order-ref" className="font-mono text-[var(--husk)]">{done.ref}</span>{t("order.refPost")}</p>
            <p data-testid="order-callback-note" className="mt-3 text-[var(--cream)]">{t("order.callOn", { phone: done.phone })}</p>
            {done.email && <p data-testid="order-email-note" className="mt-1 text-sm text-[var(--sage)]">{t("order.mailTo", { email: done.email })}</p>}
            <button data-testid="order-done-btn" onClick={() => onOpenChange(false)} className="mt-8 rounded-full bg-[var(--cream)] text-[var(--forest)] px-6 py-3 font-semibold">{t("order.done")}</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-bold tracking-tight">{t("order.title")}</DialogTitle>
              <DialogDescription className="text-[var(--sage)]">{t("order.desc")}</DialogDescription>
            </DialogHeader>
            <form data-testid="order-form" onSubmit={submit} className="grid gap-4 mt-2">
              <ClosuresNotice closures={closures} compact testId="order-closures-notice" />
              <Field id="product" label={t("order.product")}>
                <Select value={slug} onValueChange={setSlug}>
                  <SelectTrigger data-testid="order-product-select" className="h-12"><SelectValue placeholder={t("order.choose")} /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.slug} value={p.slug} data-testid={`order-product-option-${p.slug}`}>{p.name} · {p.size} · {p.stock_t} {t("order.inStock")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="qty" label={t("order.qty")}><Input id="qty" data-testid="input-quantity" type="number" min="0.5" step="0.5" required value={form.quantity_t} onChange={set("quantity_t")} className="h-12" /></Field>
                <Field id="name" label={t("order.name")}><Input id="name" data-testid="input-name" required minLength={2} value={form.name} onChange={set("name")} className="h-12" /></Field>
                <Field id="phone" label={t("order.phoneHint")}><Input id="phone" data-testid="input-phone" type="tel" required pattern="[0-9+\- ]{10,15}" value={form.phone} onChange={set("phone")} className="h-12" /></Field>
                <Field id="company" label={t("order.company")}><Input id="company" data-testid="input-company" value={form.company} onChange={set("company")} className="h-12" /></Field>
              </div>
              <Field id="email" label={t("order.emailHint")}><Input id="email" data-testid="input-email" type="email" value={form.email} onChange={set("email")} className="h-12" /></Field>
              <Field id="address" label={t("order.address")}><Textarea id="address" data-testid="input-address" required minLength={5} rows={2} value={form.address} onChange={set("address")} /></Field>
              <Field id="notes" label={t("order.notes")}><Input id="notes" data-testid="input-notes" placeholder={t("order.notesPh")} value={form.notes} onChange={set("notes")} className="h-12" /></Field>
              {product && qty > 0 && (
                <div data-testid="order-estimate" className="rounded-2xl bg-black/25 border hairline p-4 flex justify-between items-center">
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--sage)]">{t("order.estimate")}</p>
                    {qty > product.stock_t && <p className="text-xs text-amber-300 mt-1">{t("order.over")}</p>}
                  </div>
                  <p className="font-display text-2xl font-bold text-[var(--husk)]">{inr(Math.round(qty * product.price_per_t))}</p>
                </div>
              )}
              <button data-testid="order-submit-button" disabled={busy || !slug} className="mt-2 h-14 rounded-full bg-[var(--husk)] text-[var(--forest)] font-semibold inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("order.submit")}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
